import {useState} from 'react'
import {useQuery} from '@tanstack/react-query'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {Label} from '@/components/ui/label'
import {Combobox, type ComboboxOption} from '@/components/ui/combobox'
import {useFormLayout} from '@/hooks/useFormLayout'
import type {RelationshipLayoutField} from '@/types/form-layout'
import {COL_SPAN_CLASS} from '@/types/form-layout'
import {LayoutActiveIndicator} from './LayoutActiveIndicator'
import type {PartyRelationshipSchemaEntry} from '@/types/party'
import type {RelationshipFormEntry} from '@/features/onboarding/types'

/**
 * Renders relationship comboboxes according to a saved form layout.
 *
 * SECURITY: This component MUST NOT use dangerouslySetInnerHTML for any value sourced from the
 * layout schema.
 *
 * FALLBACK: If no layout exists for useCaseKey (404), renders relationships in the default schema
 * sortOrder — identical to pre-feature OrganizationStep behavior.
 */
interface RelationshipLayoutRendererProps {
    useCaseKey: string
    partyType: string
    relationships: RelationshipFormEntry[]
    onRelationshipChange: (type: string, targetId: string | null, label: string) => void
    partyRelationshipSchema: PartyRelationshipSchemaEntry[]
    templateContext?: Record<string, string>
    /**
     * Relationship type codes the caller renders elsewhere (e.g. MANAGED_BY
     * is now an Identity picker rendered by the OrganizationStep itself).
     * The layout renderer skips these so they don't double-render.
     */
    skipRelationshipTypes?: string[]
}

// ============================================================================
// Single relationship combobox
// ============================================================================

interface RelationshipComboboxProps {
    schema: PartyRelationshipSchemaEntry
    labelOverride?: string
    disabled?: boolean
    value: string | null
    selectedLabel: string
    onChange: (relationshipType: string, targetId: string | null, targetLabel: string) => void
}

function RelationshipCombobox({
    schema,
    labelOverride,
    disabled,
    value,
    selectedLabel,
    onChange,
}: RelationshipComboboxProps) {
    const services = useServices()
    const i18n = useI18n()
    const [searchQuery, setSearchQuery] = useState('')

    // Fallback: humanize the raw type code (e.g. "PART_OF" → "Part Of")
    const humanizedType = schema.relationshipType
        .toLowerCase()
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase())
    const label = labelOverride ?? schema.fromSideLabel ?? humanizedType

    const {data: searchResults, isLoading} = useQuery({
        queryKey: ['rel-search-renderer', schema.relationshipType, searchQuery],
        queryFn: () => services.parties.list({q: searchQuery, partyType: schema.toPartyType, size: 10}),
        enabled: searchQuery.length >= 2,
    })

    const options: ComboboxOption[] = (searchResults?.content ?? []).map((p) => ({
        value: String(p.id),
        label: p.derivedDisplayName,
    }))

    return (
        <div className="space-y-1.5">
            <Label>{label}</Label>
            <Combobox
                options={options}
                value={value}
                onValueChange={(val) => {
                    const party = searchResults?.content?.find((p) => String(p.id) === val)
                    onChange(schema.relationshipType, val, party?.derivedDisplayName ?? '')
                }}
                onSearch={setSearchQuery}
                placeholder={i18n.onboarding.organization.relationshipSearchPlaceholder({label})}
                isLoading={isLoading}
                selectedLabel={selectedLabel}
                data-testid={`combobox-rel-${schema.relationshipType.toLowerCase()}`}
                disabled={disabled}
            />
        </div>
    )
}

// ============================================================================
// RelationshipLayoutRenderer (main export)
// ============================================================================

export function RelationshipLayoutRenderer({
    useCaseKey,
    partyType,
    relationships,
    onRelationshipChange,
    partyRelationshipSchema,
    templateContext: _templateContext,
    skipRelationshipTypes,
}: RelationshipLayoutRendererProps) {
    const {data: layout, isLoading, isError} = useFormLayout(useCaseKey)

    const skipSet = new Set(skipRelationshipTypes ?? [])
    // Filter schema to current partyType from-side
    const applicableSchemas = partyRelationshipSchema
        .filter((s) => s.fromPartyType === partyType)
        .filter((s) => !skipSet.has(s.relationshipType))
        .sort((a, b) => a.sortOrder - b.sortOrder)

    if (applicableSchemas.length === 0) return null

    const schemaByType = new Map(applicableSchemas.map((s) => [s.relationshipType, s]))

    // Fallback: default sort order
    if (isLoading || isError || !layout) {
        return (
            <div className="space-y-4">
                {applicableSchemas.map((schema) => (
                    <RelationshipCombobox
                        key={schema.relationshipType}
                        schema={schema}
                        value={
                            relationships.find((r) => r.relationshipType === schema.relationshipType)
                                ?.targetId ?? null
                        }
                        selectedLabel={
                            relationships.find((r) => r.relationshipType === schema.relationshipType)
                                ?.targetLabel ?? ''
                        }
                        onChange={onRelationshipChange}
                    />
                ))}
            </div>
        )
    }

    // Layout-driven rendering
    return (
        <div className="space-y-4">
            <LayoutActiveIndicator useCaseKey={useCaseKey} />
            {layout.layoutSchema.sections.map((section) => {
                const relFields = section.fields.filter(
                    (f): f is RelationshipLayoutField => f.source === 'relationship'
                )

                const visibleFields = relFields.filter((f) => {
                    if (f.hidden) return false
                    return schemaByType.has(f.relationshipType)
                })

                if (visibleFields.length === 0) return null

                return (
                    <div key={section.key} className="space-y-2">
                        {section.label && (
                            <h3 className="text-sm font-semibold text-foreground" data-testid={`relationship-section-title-${section.key}`}>{section.label}</h3>
                        )}
                        <div className="grid grid-cols-12 gap-3">
                            {visibleFields.map((field, idx) => {
                                const schema = schemaByType.get(field.relationshipType)
                                if (!schema) return null

                                const defaultSpan = Math.floor(12 / (section.columns ?? 1))
                                const colSpan = Math.min(Math.max(field.colSpan ?? defaultSpan, 1), 12)

                                return (
                                    <div key={idx} className={COL_SPAN_CLASS[colSpan]}>
                                        <RelationshipCombobox
                                            schema={schema}
                                            labelOverride={field.label}
                                            disabled={field.disabled}
                                            value={
                                                relationships.find(
                                                    (r) => r.relationshipType === field.relationshipType
                                                )?.targetId ?? null
                                            }
                                            selectedLabel={
                                                relationships.find(
                                                    (r) => r.relationshipType === field.relationshipType
                                                )?.targetLabel ?? ''
                                            }
                                            onChange={onRelationshipChange}
                                        />
                                    </div>
                                )
                            })}
                        </div>
                    </div>
                )
            })}
        </div>
    )
}
